import { css } from '@emotion/core';
import { LecturePage, NLecturePage } from 'redux/schemas/models/lecture-page';
import useUserColors, { COLOR_LIST_LIMIT, DEFAULT_COLOR_PALETTE } from 'shared/hooks/use-user-colors';
import { gray6, getColorBrightness, gray5 } from 'styles/global_defaults/colors';

// will be used to determine when to add border to the color button in case it
// is too bright for the white background. See the <ColorButton /> implementation
const DEFAULT_BORDER_COLOR = gray6;
const DEFAULT_BORDER_COLOR_BRIGHTNESS = getColorBrightness(DEFAULT_BORDER_COLOR);

const colorButtonStyle = css`
  padding: 0;
  border-style: solid;
  border-color: ${DEFAULT_BORDER_COLOR};
  border-radius: 100%;

  &:focus {
    outline: none;
  }
`;

interface ColorButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  active: boolean;
  color: string;
}

/**
 * <ColorButton /> is a circular button with a color that has an `active` state
 */
function ColorButton(props: ColorButtonProps) {
  const { active, color, ...rest } = props;

  let size = '16px';
  let borderWidth = '0px';

  // if the color brightness is too high, we add an small border
  const colorBrightness = getColorBrightness(props.color);
  if (colorBrightness > DEFAULT_BORDER_COLOR_BRIGHTNESS) {
    size = '16px';
    borderWidth = '1px';
  }

  if (props.active) {
    size = '22px';
    borderWidth = '3px';
  }

  return (
    <button
      type='button'
      aria-label={props.color}
      css={css`
        ${colorButtonStyle};
        ${props.active && 'box-shadow: 2px 2px 4px 0 #1d2126'};
        width: ${size};
        height: ${size};
        background-color: ${props.color};
        border-width: ${borderWidth};
      `}
      {...rest}
    />
  );
}

const buttonContainerStyle = css`
  margin-bottom: 7px;
  width: 26px;
  height: 22px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

interface ColorListProps {
  activeColor: string;
  colorList: string[];
  onChange(newColor: string): void;
}

/**
 * <ColorList /> is a component to show a collection of <ColorButton />
 */
function ColorList(props: ColorListProps) {
  return (
    <div
      css={css`
        width: 156px;
        display: flex;
        flex-wrap: wrap;
      `}
    >
      {props.colorList.map((color) => (
        <div key={color} css={buttonContainerStyle}>
          <ColorButton
            color={color}
            active={color === props.activeColor}
            onClick={() => {
              props.onChange(color);
            }}
          />
        </div>
      ))}
    </div>
  );
}

//
// <ColorPalette />
//
const colorListSectionStyle = css`
  padding: 19px 14px 10px 14px;
`;

interface ColorPaletteProps {
  /** currently selected color */
  activeColor: string;

  /** triggered when a color is clicked */
  onChange(newColor: string): void;

  /** Keeps the color palette colors in the initial order when selected */
  preserveColorOrder?: boolean;

  currentLecture: NLecturePage;
}

/**
 * UI with a collection of color buttons with recently used colors + a list of
 * predefined colors
 */
function ColorPalette(props: ColorPaletteProps) {
  const { updateRecentlyUsedColorsIfNeeded, brandColors, recentlyUsedColors } = useUserColors({
    currentLecture: props.currentLecture,
  });

  function handleOnChange(color: string) {
    updateRecentlyUsedColorsIfNeeded(color, props.preserveColorOrder);
    props.onChange(color);
  }

  const userColors = [...brandColors, ...recentlyUsedColors].slice(0, COLOR_LIST_LIMIT);

  return (
    <div>
      <div
        css={css`
          ${colorListSectionStyle};
          background-color: ${gray5};
        `}
      >
        <ColorList onChange={handleOnChange} colorList={userColors} activeColor={props.activeColor} />
      </div>
      <div
        css={css`
          ${colorListSectionStyle};
        `}
      >
        <ColorList onChange={handleOnChange} colorList={DEFAULT_COLOR_PALETTE} activeColor={props.activeColor} />
      </div>
    </div>
  );
}

export default ColorPalette;
